import { FC, useEffect, useState, useCallback } from 'react';
import { useMedia } from 'react-use';
import classNames from 'classnames';
import LottiePlayer from 'lottie-react';

import { animationMap } from '../../../CalculationContainer/utils';
import personFallback from '../../../../../../assets/animations/metro/persons/person1.json';
import { ReactComponent as MetroRulesBg } from '../../../../../../assets/images/metro/MetroRulesBg.svg';
import { ReactComponent as MetroRulesBgTablet } from '../../../../../../assets/images/metro/MetroRulesBgTablet.svg';

import MetroBuilding from '../../../CalculationContainer/MetroBuilding';

import styles from './styles.module.scss';

import { minDesktopWidth } from '../../../../../../shared/widths';

interface PersonAnimation {
  id: number;
  lottieId: number;
  direction: 'in' | 'out';
}

const MetroEntranceScene: FC = () => {
  const [step, setStep] = useState(0);

  const [isLeftOpenDoor, setIsLeftOpenDoor] = useState(false);
  const [isRightOpenDoor, setIsRightOpenDoor] = useState(false);

  const [persons, setPersons] = useState<PersonAnimation[]>([]);

  const isDesktop = useMedia(`(min-width: ${minDesktopWidth}px)`);

  const getTwoRandomPersonIds = useCallback((): number[] => {
    const possibleIds = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    const shuffled = [...possibleIds].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 2);
  }, []);

  useEffect(() => {
    switch (step) {
      case 0:
        setTimeout(() => setStep(1), 500);
        break;

      case 1:
        setIsLeftOpenDoor(true);
        setTimeout(() => setStep(2), 300);
        break;

      case 2: {
        const [firstPersonId, secondPersonId] = getTwoRandomPersonIds();
        setPersons([
          {
            id: Date.now(),
            lottieId: firstPersonId,
            direction: 'in',
          },
          {
            id: Date.now() + 1,
            lottieId: secondPersonId,
            direction: 'in',
          },
        ]);

        setTimeout(() => {
          setPersons([]);
          setStep(3);
        }, 1200);
        break;
      }

      case 3:
        setIsLeftOpenDoor(false);
        setTimeout(() => setStep(4), 800);
        break;

      case 4:
        setIsRightOpenDoor(true);
        setTimeout(() => setStep(5), 0);
        break;

      case 5: {
        const [firstPersonId, secondPersonId] = getTwoRandomPersonIds();
        setPersons([
          {
            id: Date.now(),
            lottieId: firstPersonId,
            direction: 'out',
          },
          {
            id: Date.now() + 1,
            lottieId: secondPersonId,
            direction: 'out',
          },
        ]);

        setTimeout(() => {
          setPersons([]);
          setStep(6);
        }, 2000);
        break;
      }

      case 6:
        setIsRightOpenDoor(false);
        setTimeout(() => setStep(0), 1000);
        break;

      default:
        break;
    }
  }, [step, getTwoRandomPersonIds]);

  return (
    <div className={styles.container}>
      {isDesktop ? <MetroRulesBg className={styles.background} /> : <MetroRulesBgTablet className={styles.background} />}
      <MetroBuilding isLeftOpenDoor={isLeftOpenDoor} isRightOpenDoor={isRightOpenDoor} className={styles.metro} />

      <div className={styles.groupsContainer}>
        {persons.map((person) => (
          <div
            key={person.id}
            className={classNames(
              styles.personWrapper,
              { [styles.personIn]: person.direction === 'in' },
              { [styles.personOut]: person.direction === 'out' },
            )}
          >
            <LottiePlayer animationData={animationMap[person.lottieId] || personFallback} autoPlay loop className={styles.lottiePerson} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default MetroEntranceScene;
